import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { facilitiesApi } from "@/features/facilities/api";
import { OnboardingHero } from "@/features/facilities/OnboardingHero";
import type { Facility, ItemStatus } from "@/features/facilities/types";
import { reportsApi } from "@/features/reports/api";
import { tasksApi, insightsApi } from "@/features/tasks/api";

type FacilitySummary = {
  openReports: number;
  breachedReports: number;
  itemsAttention: number;
  tasksDue: number;
  proposalsPending: number;
  statusCounts: Record<ItemStatus, number>;
  totalItems: number;
};

const STATUS_DOT: Record<ItemStatus, string> = {
  ok: "bg-emerald-500",
  minor: "bg-amber-500",
  major: "bg-orange-500",
  out_of_service: "bg-rose-500",
  unknown: "bg-slate-400",
};

const STATUS_ORDER: ItemStatus[] = [
  "ok",
  "minor",
  "major",
  "out_of_service",
  "unknown",
];

export function DashboardPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["facilities"],
    queryFn: () => facilitiesApi.list(),
  });

  // Defensive against either pagination shape or raw array — same guard
  // as before, so a malformed response doesn't whitescreen.
  const facilities: Facility[] = Array.isArray(data as unknown)
    ? (data as unknown as Facility[])
    : (data?.results ?? []);

  const summaries = useQueries({
    queries: facilities.map((f) => ({
      queryKey: ["dashboard-summary", f.id],
      queryFn: () => fetchFacilitySummary(f.id),
      // Stat cards aren't worth fetching every second; a minute of
      // staleness is fine and keeps things calm when the user bounces
      // around routes.
      staleTime: 60_000,
    })),
  });

  return (
    <section className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t("dashboard.title")}</h1>
        <Link
          to="/facilities"
          className="text-sm text-brand-600 hover:underline"
        >
          {t("facilities.manage")}
        </Link>
      </header>

      {isLoading ? (
        <p className="text-slate-500">{t("common.loading")}</p>
      ) : facilities.length === 0 ? (
        <OnboardingHero />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {facilities.map((f, i) => (
            <FacilityCard
              key={f.id}
              facility={f}
              summary={summaries[i]?.data}
              loading={summaries[i]?.isLoading ?? true}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function FacilityCard({
  facility,
  summary,
  loading,
}: {
  facility: Facility;
  summary: FacilitySummary | undefined;
  loading: boolean;
}) {
  const { t } = useTranslation();

  const headline = (() => {
    if (loading || !summary) return null;
    if (summary.openReports === 0) {
      return (
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
          {t("dashboard.all_clear")}
        </span>
      );
    }
    return (
      <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
        {t(
          summary.openReports === 1
            ? "dashboard.open_count_one"
            : "dashboard.open_count_other",
          { count: summary.openReports },
        )}
      </span>
    );
  })();

  return (
    <Link
      to={`/facilities/${facility.id}`}
      className="group flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-brand-500/40 hover:bg-brand-50/30"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold">{facility.name}</p>
          {facility.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">
              {facility.description}
            </p>
          )}
        </div>
        {headline}
      </header>

      <StatusStrip
        counts={summary?.statusCounts}
        total={summary?.totalItems ?? 0}
        loading={loading}
      />

      <dl className="space-y-1.5 text-xs">
        <Stat
          icon="🛠️"
          label={
            summary && summary.itemsAttention > 0
              ? t(
                  summary.itemsAttention === 1
                    ? "dashboard.items_attention_one"
                    : "dashboard.items_attention_other",
                  { count: summary.itemsAttention },
                )
              : null
          }
        />
        <Stat
          icon="⏰"
          label={
            summary && summary.tasksDue > 0
              ? t(
                  summary.tasksDue === 1
                    ? "dashboard.tasks_due"
                    : "dashboard.tasks_due_other",
                  { count: summary.tasksDue },
                )
              : null
          }
        />
        <Stat
          icon="✨"
          label={
            summary && summary.proposalsPending > 0
              ? t(
                  summary.proposalsPending === 1
                    ? "dashboard.proposals_one"
                    : "dashboard.proposals_other",
                  { count: summary.proposalsPending },
                )
              : null
          }
        />
        <Stat
          icon="🚨"
          tone="rose"
          label={
            summary && summary.breachedReports > 0
              ? t(
                  summary.breachedReports === 1
                    ? "dashboard.breached_one"
                    : "dashboard.breached_other",
                  { count: summary.breachedReports },
                )
              : null
          }
        />
      </dl>

      <footer className="mt-auto pt-1 text-xs font-medium text-brand-600 group-hover:underline">
        {t("dashboard.open_facility")}
      </footer>
    </Link>
  );
}

function Stat({
  icon,
  label,
  tone,
}: {
  icon: string;
  label: string | null;
  tone?: "rose";
}) {
  if (!label) return null;
  return (
    <div
      className={
        "flex items-center gap-2 " +
        (tone === "rose" ? "text-rose-700" : "text-slate-700")
      }
    >
      <span aria-hidden className="text-sm leading-none">
        {icon}
      </span>
      <span>{label}</span>
    </div>
  );
}

function StatusStrip({
  counts,
  total,
  loading,
}: {
  counts: Record<ItemStatus, number> | undefined;
  total: number;
  loading: boolean;
}) {
  const { t } = useTranslation();
  if (loading) {
    return <div className="h-2 w-full animate-pulse rounded-full bg-slate-100" />;
  }
  if (!counts || total === 0) {
    return (
      <p className="text-xs italic text-slate-400">
        {t("dashboard.empty_facility")}
      </p>
    );
  }
  return (
    <div
      className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100"
      title={`${total} items`}
    >
      {STATUS_ORDER.map((s) => {
        const n = counts[s];
        if (!n) return null;
        const pct = (n / total) * 100;
        return (
          <span
            key={s}
            className={STATUS_DOT[s]}
            style={{ width: `${pct}%` }}
            aria-label={`${s}: ${n}`}
          />
        );
      })}
    </div>
  );
}

async function fetchFacilitySummary(facilityId: number): Promise<FacilitySummary> {
  const [items, submitted, ack, prog, activeTasks, proposed, insights] =
    await Promise.all([
      facilitiesApi.listItems(facilityId),
      reportsApi.list({ facility: facilityId, status: "submitted" }),
      reportsApi.list({ facility: facilityId, status: "acknowledged" }),
      reportsApi.list({ facility: facilityId, status: "in_progress" }),
      tasksApi.list(facilityId, "active"),
      tasksApi.list(facilityId, "proposed"),
      insightsApi.list(facilityId),
    ]);

  const allItems = items.results;
  const statusCounts: Record<ItemStatus, number> = {
    ok: 0,
    minor: 0,
    major: 0,
    out_of_service: 0,
    unknown: 0,
  };
  let itemsAttention = 0;
  for (const it of allItems) {
    statusCounts[it.current_status]++;
    if (it.current_status === "major" || it.current_status === "out_of_service")
      itemsAttention++;
  }

  const openReports = [
    ...submitted.results,
    ...ack.results,
    ...prog.results,
  ];
  const now = Date.now();
  const breachedReports = openReports.filter(
    (r) => r.sla_breach_at && new Date(r.sla_breach_at).getTime() < now,
  ).length;

  // Active task is "due" if next_due_at is within the next 7 days OR
  // already past (overdue). Tasks with no next_due_at are excluded —
  // they're one-offs without a schedule.
  const horizon = now + 7 * 24 * 60 * 60 * 1000;
  const tasksDue = activeTasks.results.filter((task) => {
    if (!task.next_due_at) return false;
    return new Date(task.next_due_at).getTime() <= horizon;
  }).length;

  return {
    openReports: submitted.count + ack.count + prog.count,
    breachedReports,
    itemsAttention,
    tasksDue,
    proposalsPending: proposed.count + insights.count,
    statusCounts,
    totalItems: allItems.length,
  };
}
